"use client";

import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { SectionHeading } from "@/components/ui/section-heading";
import { LinkButton, GetEarlyAccessButton } from "@/components/ui/buttons";
import { fadeUp, stagger } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { GeoAccent, type AccentVariant } from "@/components/ui/geo-art";
import type { PolyShape } from "@/components/three/poly-canvas";

/** three.js is client-only; the hero object loads after the text. */
const PolyCanvas = dynamic(() => import("@/components/three/poly-canvas"), { ssr: false });

/**
 * Building blocks for the audience pages (`/developers`, `/enterprise`,
 * `/about`), added 2026-10-13 with the navbar restructure.
 *
 * They reuse the PK Endpoint page's vocabulary on purpose: the same hero frame
 * as `/pricing` and `/models`, the same `SectionHeading`, the same hoverable
 * card. A visitor moving between these pages and the existing ones should not
 * be able to tell which were written later.
 */

const CARD =
  "rounded-lg p-6 outline-none transition-all duration-300 hover:-translate-y-0.5 hover:bg-card hover:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.3)] hover:ring-1 hover:ring-inset hover:ring-border-strong focus:-translate-y-0.5 focus:bg-card focus:shadow-[0_16px_40px_-24px_rgba(0,0,0,0.3)] focus:ring-1 focus:ring-inset focus:ring-border-strong";

export type InfoCta =
  | { kind: "early-access"; label: string }
  | { kind: "link"; label: string; href: string; secondary?: boolean };

export function PageIntro({
  eyebrow,
  title,
  lead,
  ctas = [],
  art,
}: {
  eyebrow: string;
  title: React.ReactNode;
  lead: React.ReactNode;
  ctas?: InfoCta[];
  /**
   * A sharp solid drawn in points beside the text (2026-10-21), the home
   * sphere's and the PK octahedron's family. Laid out as the PK hero lays out
   * its octahedron: a column from `lg`, below the text on a phone.
   */
  art?: PolyShape;
}) {
  return (
    <section className="relative overflow-hidden pb-6 pt-28 sm:pb-10 sm:pt-36">
      <div
        className={cn(
          "mx-auto grid w-full max-w-[90rem] items-center px-5 sm:px-8",
          art && "gap-6 lg:grid-cols-[1.05fr_1fr] lg:gap-10",
        )}
      >
        <motion.div
          variants={stagger(0.12)}
          initial="hidden"
          animate="show"
          className="min-w-0"
        >
          <motion.p variants={fadeUp} className="eyebrow mb-4">
            {eyebrow}
          </motion.p>
          <motion.h1
            variants={fadeUp}
            className="max-w-4xl font-serif text-[clamp(2rem,4.6vw,3.8rem)] font-light leading-[1.06] tracking-[-0.015em]"
          >
            {title}
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="mt-5 max-w-2xl font-serif text-[clamp(1.1rem,1.7vw,1.35rem)] font-light leading-[1.3] text-foreground/85"
          >
            {lead}
          </motion.p>
          {ctas.length > 0 && (
            <motion.div variants={fadeUp} className="mt-9 flex flex-wrap items-center gap-3">
              {ctas.map((c) =>
                c.kind === "early-access" ? (
                  <GetEarlyAccessButton key={c.label}>{c.label}</GetEarlyAccessButton>
                ) : (
                  <LinkButton
                    key={c.label}
                    href={c.href}
                    variant={c.secondary ? "secondary" : "primary"}
                    withArrow={!c.secondary}
                  >
                    {c.label}
                  </LinkButton>
                ),
              )}
            </motion.div>
          )}
        </motion.div>
        {art && (
          <div className="relative h-[38vh] min-h-[260px] w-full lg:h-[62vh] lg:max-h-[38rem]">
            <div className="absolute inset-0 lg:-right-[6%]">
              <PolyCanvas shape={art} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export interface InfoItem {
  title: string;
  body: React.ReactNode;
}

/** A heading, an optional paragraph, and a grid of the same cards PK Endpoint uses. */
export function CardSection({
  id,
  eyebrow,
  title,
  intro,
  items,
  columns = 2,
}: {
  id?: string;
  eyebrow?: string;
  title: React.ReactNode;
  intro?: React.ReactNode;
  items: InfoItem[];
  columns?: 2 | 3;
}) {
  return (
    <section id={id} className="relative scroll-mt-24">
      <div className="mx-auto max-w-[90rem] px-5 py-14 sm:px-8 sm:py-24">
        <SectionHeading align="left" eyebrow={eyebrow} title={title} />
        {intro && <p className="mt-5 max-w-2xl font-sans text-sm leading-relaxed text-muted">{intro}</p>}
        <motion.div
          variants={stagger(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className={cn("mt-12 grid gap-4", columns === 3 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2")}
        >
          {items.map((it) => (
            <motion.div key={it.title} variants={fadeUp} tabIndex={0} className={CARD}>
              <h3 className="font-serif text-xl font-light leading-snug">{it.title}</h3>
              <div className="mt-3 font-sans text-sm leading-relaxed text-muted">{it.body}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/** Prose under a heading, for the passages that are an argument rather than a list. */
export function ProseSection({
  id,
  eyebrow,
  title,
  children,
  accent,
}: {
  id?: string;
  eyebrow?: string;
  title: React.ReactNode;
  children: React.ReactNode;
  /** A small sharp shape in the empty right of the section, from `lg` up. */
  accent?: AccentVariant;
}) {
  return (
    <section id={id} className="relative scroll-mt-24">
      <div
        className={cn(
          "mx-auto max-w-[90rem] px-5 py-14 sm:px-8 sm:py-24",
          accent && "lg:grid lg:grid-cols-[minmax(0,1fr)_13rem] lg:gap-16 xl:grid-cols-[minmax(0,1fr)_15rem]",
        )}
      >
        <div className="min-w-0">
          <SectionHeading align="left" eyebrow={eyebrow} title={title} />
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="mt-6 flex max-w-3xl flex-col gap-4 font-sans text-[0.95rem] leading-relaxed text-muted"
          >
            {children}
          </motion.div>
        </div>
        {accent && (
          <div className="hidden pt-10 lg:block">
            <GeoAccent variant={accent} />
          </div>
        )}
      </div>
    </section>
  );
}

/** A plain bulleted list inside a card or prose block, in the cards' muted ink. */
export function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="mt-1 flex flex-col gap-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span aria-hidden="true" className="mt-[0.55rem] h-1 w-1 shrink-0 rounded-full bg-muted-2" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
